import axios, { AxiosInstance } from 'axios';
import Swal from 'sweetalert2';
import { notif } from "@/plugins/notyf";
import global from '@/global';

if (typeof window !== 'undefined' && window.Storage && !window.__storage_proxy_initialized) {
  window.__storage_proxy_initialized = true;
  const origGetItem = Storage.prototype.getItem;
  const CLIENT_KEYS = ['idcliente', 'id_cliente_seleccionado', 'idclient'];

  Storage.prototype.getItem = function (key) {
    if (this === window.localStorage && CLIENT_KEYS.includes(key)) {
      return (
        sessionStorage.getItem('id_cliente_seleccionado') ||
        sessionStorage.getItem('idcliente') ||
        sessionStorage.getItem('idclient') ||
        origGetItem.call(this, key)
      );
    }
    return origGetItem.call(this, key);
  };
}

let api = AxiosInstance
// Flag global para mostrar el modal solo una vez
let isSessionExpired = false;
let isRateLimited = false;
let isRedirectingClientNotFound = false;

export function handleClienteNoEncontrado(message) {
  if (isRedirectingClientNotFound) return;
  isRedirectingClientNotFound = true;

  localStorage.removeItem('idcliente');
  localStorage.removeItem('id_cliente_seleccionado');
  localStorage.removeItem('idclient');
  localStorage.removeItem('id_cliente');
  localStorage.removeItem('cliente');

  sessionStorage.removeItem('idcliente');
  sessionStorage.removeItem('id_cliente_seleccionado');
  sessionStorage.removeItem('idclient');
  sessionStorage.removeItem('id_cliente');
  sessionStorage.removeItem('cliente');

  sessionStorage.setItem(
    'tenant_offline_message',
    message || 'El cliente solicitado no existe en el sistema.'
  );

  window.location.href = '/seleccionarCliente';
}

export function createApi() {
  // Here we set the base URL for all requests made to the api
  api = axios.create({
    baseURL: global.ruta_api,
  });
  // We set an interceptor for each request to
  // include Bearer token and X-Tenant-Id header if user is logged in & client selected
  api.interceptors.request.use((config) => {
    let token = localStorage.getItem('token');
    config.headers = config.headers || {};

    if (localStorage.getItem('logueo') && token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const selectedClientId =
      sessionStorage.getItem('id_cliente_seleccionado') ||
      sessionStorage.getItem('idcliente') ||
      sessionStorage.getItem('idclient') ||
      localStorage.getItem('id_cliente_seleccionado') ||
      localStorage.getItem('idcliente') ||
      localStorage.getItem('idclient');

    if (selectedClientId) {
      config.headers['X-Tenant-Id'] = selectedClientId;
    }

    return config;
  }, (error) => {
    return Promise.reject(error);
  })

  // RESPONSE INTERCEPTOR - Manejo simple de errores
  api.interceptors.response.use(
    response => {
      const data = response?.data;
      if (data && (data.error === 'cliente_no_encontrado' || (data.status === false && data.error === 'cliente_no_encontrado'))) {
        handleClienteNoEncontrado(data.message);
        return new Promise(() => { });
      }
      return response;
    },
    async error => {
      const originalRequest = error.config;
      const { status } = error.response || {};
      const data = error.response?.data || error.data;

      // Manejo de error cliente_no_encontrado
      if (data && (data.error === 'cliente_no_encontrado' || (data.status === false && data.error === 'cliente_no_encontrado'))) {
        handleClienteNoEncontrado(data.message);
        return new Promise(() => { });
      }

      // Manejo de error 401 - Token expirado
      if (status === 401 && window.location.pathname !== "/login") {

        // Si ya se mostró el modal de sesión expirada, no hacer nada
        if (isSessionExpired) {
          return new Promise(() => { }); // prevent further handling
        }


        // Marcar que ya se está mostrando el modal
        isSessionExpired = true;

        // Detectar el motivo del 401 desde el header X-Auth-Reason
        // Axios normaliza los headers a minúsculas
        const headers = error.response?.headers || {};
        console.log('[401 Debug] All headers:', headers);

        const authReason = headers['x-auth-reason'] ||
          headers['X-Auth-Reason'] ||
          headers['X-AUTH-REASON'] ||
          'expired';

        console.log('[401 Debug] Auth reason:', authReason);

        let modalConfig;

        if (authReason === 'revoked') {
          // Sesión revocada por login desde otro dispositivo
          modalConfig = {
            title: "Sesión cerrada",
            icon: "info",
            html: `Has iniciado sesión desde otro dispositivo.<br><br>Esta sesión ha sido cerrada.<br><br>Redirigiendo al login en <b></b> segundos...`,
            iconColor: "#3085d6"
          };
        } else {
          // Sesión expirada por inactividad (default)
          modalConfig = {
            title: "Sesión expirada",
            icon: "warning",
            html: `Tu sesión ha expirado por inactividad en el sistema.<br><br>Redirigiendo al login en <b></b> segundos...`,
            iconColor: "#f8bb86"
          };
        }

        // Mostrar modal con timer de 10 segundos
        let timerInterval;
        Swal.fire({
          ...modalConfig,
          timer: 10000, // 10 segundos
          timerProgressBar: true,
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            const b = Swal.getHtmlContainer().querySelector('b');
            timerInterval = setInterval(() => {
              b.textContent = Math.ceil(Swal.getTimerLeft() / 1000);
            }, 100);
          },
          willClose: () => {
            clearInterval(timerInterval);
          }
        }).then(() => {
          // Limpiar datos locales después del timer
          localStorage.clear();
          sessionStorage.clear();

          // Redirigir al login
          window.location.href = "/login";
        });

        return new Promise(() => { }); // prevent further handling
      }

      // Manejo de error 429 - Demasiadas peticiones
      if (status === 429) {
        if (!isRateLimited) {
          isRateLimited = true;
          Swal.fire({
            title: "¡Demasiadas peticiones!",
            icon: "warning",
            html: `
              Has realizado muchas peticiones en poco tiempo.<br><br>
              Por favor, <b>espera un momento</b> antes de continuar.
            `,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#f8bb86",
          }).then(() => {
            isRateLimited = false;
          });
        }
        return new Promise(() => { }); // Prevenir propagación
      }

      // Manejo de error 503 - Base de datos del cliente (tenant) temporalmente inaccesible
      // IMPORTANTE: Guardar mensaje en sessionStorage y redirigir.
      // El componente SeleccionarCliente se encargará de mostrar el Swal para evitar que se pierda durante la transición.
      if (status === 503) {
        sessionStorage.setItem('tenant_offline_message', 'La base de datos de este cliente está temporalmente inaccesible.');

        // Quitar cliente del almacenamiento local inmediatamente para evitar bloqueos
        localStorage.removeItem('idcliente');
        localStorage.removeItem('id_cliente_seleccionado');
        localStorage.removeItem('idclient');

        // Redirigir físicamente al menú de selección de cliente
        window.location.href = '/seleccionarCliente';

        return new Promise(() => { }); // Prevenir propagación
      }

      // Manejo de otros errores del servidor (excluye 503 para no recargar por fallos de tenant)
      if (status && ![400, 401, 403, 422, 429, 503].includes(status) && window.location.pathname !== "/login") {
        Swal.fire({
          title: "Ups, ¡algo pasó!",
          icon: "error",
          html: `
            Prueba actualizando la ventana.<br><br>
            Si el problema persiste, por favor contacta a <a href="mailto:${global.support_email}">${global.support_email}</a>, de preferencia adjuntando capturas de pantalla.
          `,
          confirmButtonText: "Actualizar",
          confirmButtonColor: "#d33",
        }).then((result) => {
          if (result.isConfirmed) {
            location.reload();
          }
        });

        return new Promise(() => { });
      }

      // Manejo de errores de validación
      if (status && [400, 401, 403, 422].includes(status) && window.location.pathname !== "/login") {
        if (data.message === "Error de validación") {
          return Promise.resolve({
            data: { success: false, message: data.message },
            errorHandled: true,
            status,
          });
        } else {
          notif.error(data.message);
          return Promise.resolve({
            data: { success: false, message: data.message },
            errorHandled: true,
            status,
          });
        }
      }
      return Promise.reject(error);
    }
  );

  return api
}

const useApi = () => {
  //console.log(global);
  if (!api) {
    createApi();
  }
  return api;
}

export const axios2 = useApi();
